import React from 'react';
import { FaPlay, FaPlus, FaClock, FaCheckCircle, FaLightbulb } from 'react-icons/fa';

const ResumeSessionDialog = ({ resumeData, onContinue, onStartNew, onClose }) => {
    const getStatusInfo = (status) => {
        const statusConfig = {
            'objectives_ready': {
                icon: FaLightbulb,
                color: 'text-blue-500',
                bgColor: 'bg-blue-50',
                title: 'Objetivos Generados',
                description: 'Tienes objetivos listos para seleccionar'
            },
            'strategy_ready': {
                icon: FaCheckCircle,
                color: 'text-orange-500',
                bgColor: 'bg-orange-50',
                title: 'Estrategias Generadas',
                description: 'Tienes estrategias listas para elegir'
            },
            'tactics_ready': {
                icon: FaCheckCircle,
                color: 'text-green-500',
                bgColor: 'bg-green-50',
                title: 'Tácticas Generadas',
                description: 'Tienes tácticas listas para revisar'
            }
        };

        return statusConfig[status] || {
            icon: FaClock,
            color: 'text-gray-500',
            bgColor: 'bg-gray-50',
            title: 'Sesión en Progreso',
            description: 'Tienes una sesión pendiente'
        };
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const statusInfo = getStatusInfo(resumeData.status);
    const StatusIcon = statusInfo.icon;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-gray-900">
                            Continuar Sesión
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Status Badge */}
                    <div className={`${statusInfo.bgColor} rounded-lg p-4 mb-6`}>
                        <div className="flex items-center">
                            <StatusIcon className={`w-6 h-6 mr-3 ${statusInfo.color}`} />
                            <div>
                                <h3 className="font-semibold text-gray-900">{statusInfo.title}</h3>
                                <p className="text-sm text-gray-600">{statusInfo.description}</p>
                            </div>
                        </div>
                    </div>

                    {/* Session Info */}
                    <div className="space-y-4 mb-6">
                        <div>
                            <h4 className="font-medium text-gray-900 mb-2">Detalles de la Sesión</h4>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Objetivo:</span>
                                    <span className="text-sm font-medium text-gray-900 capitalize">
                                        {resumeData.generalObjective}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Creada:</span>
                                    <span className="text-sm text-gray-900">
                                        {formatDate(resumeData.createdAt)}
                                    </span>
                                </div>
                                {resumeData.selectedObjective && (
                                    <div className="pt-2 border-t border-gray-200">
                                        <span className="text-sm text-gray-600">Objetivo seleccionado:</span>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {resumeData.selectedObjective.title}
                                        </p>
                                    </div>
                                )}
                                {resumeData.selectedStrategy && (
                                    <div className="pt-2 border-t border-gray-200">
                                        <span className="text-sm text-gray-600">Estrategia seleccionada:</span>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {resumeData.selectedStrategy.title}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {resumeData.nextAction && (
                            <div className="bg-blue-50 rounded-lg p-4">
                                <h4 className="font-medium text-blue-900 mb-2">Próxima Acción</h4>
                                <p className="text-sm text-blue-700">{resumeData.nextAction}</p>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                        <button
                            onClick={onContinue}
                            className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center"
                        >
                            <FaPlay className="w-4 h-4 mr-2" />
                            Continuar desde donde me quedé
                        </button>

                        <button
                            onClick={onStartNew}
                            className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
                        >
                            <FaPlus className="w-4 h-4 mr-2" />
                            Crear nueva propuesta
                        </button>
                    </div>

                    {/* Info */}
                    <div className="mt-4 text-center">
                        <p className="text-xs text-gray-500">
                            Al continuar, recuperarás todo tu progreso anterior
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResumeSessionDialog; 